import {buildStreetWithHouseNumber, type GeoRefAddress} from "@reva/shared/geo-reference";

/** Vergleichsform: klein, ohne Leerzeichen/Punkte, „straße/str." vereinheitlicht. */
function normalize(value: string | undefined | null): string {
    return (value ?? "")
        .toLowerCase()
        .replace(/straße|strasse|str\./g, "str")
        .replace(/[\s.,-]/g, "");
}

/** true, wenn beide Werte vorhanden sind und sich nach Normalisierung unterscheiden. */
function bothPresentAndDifferent(a: string | undefined | null, b: string | undefined | null): boolean {
    const na = normalize(a);
    const nb = normalize(b);
    return na.length > 0 && nb.length > 0 && na !== nb;
}

/**
 * Zerlegt die Straße einer Adresse („Römerstr. 24a“) in Straßenname und (optionale) Hausnummer. Eine separat
 * gespeicherte Hausnummer (straßenabschnittsgenaue Treffer, #19746) wird vorher angehängt.
 */
function splitStreet(address: Pick<GeoRefAddress, "street" | "houseNumber"> | null | undefined): {
    name: string;
    number: string;
} {
    const street = buildStreetWithHouseNumber(address?.street, address?.houseNumber);
    const match = street.match(/^(.*?)\s+(\d[\w/-]*)$/);
    return match ? {name: match[1], number: match[2]} : {name: street, number: ""};
}

/**
 * #19159: Widerspricht die aus dem Exposé georeferenzierte Adresse der Adresse der geladenen Bewertung?
 *
 * Verglichen werden nur Felder, die auf BEIDEN Seiten vorliegen (PLZ, Gemeinde, Straßenname, Hausnummer).
 * Ein Exposé, das nur den Ort („Bobingen“) oder nur die Straße ohne Hausnummer nennt, widerspricht
 * „Römerstr. 24, 86399 Bobingen“ also nicht; „Römerstr. 25“ dagegen schon. Fehlt eine der beiden
 * Adressen ganz, gibt es keinen Widerspruch.
 */
export function isAddressContradictory(imported: GeoRefAddress | null, current: GeoRefAddress | null): boolean {
    if (!imported || !current) return false;
    const importedStreet = splitStreet(imported);
    const currentStreet = splitStreet(current);
    return (
        bothPresentAndDifferent(imported.postcode, current.postcode) ||
        bothPresentAndDifferent(imported.municipality, current.municipality) ||
        bothPresentAndDifferent(importedStreet.name, currentStreet.name) ||
        bothPresentAndDifferent(importedStreet.number, currentStreet.number)
    );
}

/** Eine Zeile des Adressvergleichs im Konfliktdialog. */
export interface AddressComparisonRow {
    field: "street" | "houseNumber" | "postcode" | "municipality";
    imported: string;
    current: string;
    /** nach denselben Regeln wie {@link isAddressContradictory} ein Widerspruch */
    differs: boolean;
}

/**
 * Stellt die Adressteile beider Seiten nebeneinander, damit der Konfliktdialog zeigen kann, woran der
 * Widerspruch hängt. Zeilen, die auf keiner Seite belegt sind, fallen weg.
 */
export function compareAddresses(
    imported: GeoRefAddress | null,
    current: GeoRefAddress | null,
): AddressComparisonRow[] {
    const importedStreet = splitStreet(imported);
    const currentStreet = splitStreet(current);
    const pairs: [AddressComparisonRow["field"], string | undefined, string | undefined][] = [
        ["street", importedStreet.name, currentStreet.name],
        ["houseNumber", importedStreet.number, currentStreet.number],
        ["postcode", imported?.postcode, current?.postcode],
        ["municipality", imported?.municipality, current?.municipality],
    ];
    return pairs
        .filter(([, a, b]) => (a ?? "").trim() || (b ?? "").trim())
        .map(([field, a, b]) => ({
            field,
            imported: (a ?? "").trim(),
            current: (b ?? "").trim(),
            differs: bothPresentAndDifferent(a, b),
        }));
}
